"use client";

import { useEffect, useRef, useState } from "react";
import { accent } from "@/lib/palette";

const pythonCode = `import qosm

q = qosm.init(capabilities={
    "read_file": qosm.fs("/data/*", read_only=True),
    "post_msg":  qosm.slack("#analytics"),
    "model":      qosm.llm("gpt-4o", max_tokens=1024),
})

result = q.run("@acme/analytics", {
    "input": "Summarize Q4 revenue by region"
})

print(result)`;

const consoleOutput = `> python main.py

Resolving @acme/analytics...
  Capabilities: read_file, post_msg, model
  Attenuations: path=/data/*, channel=#analytics

Ok {
  value: {
    summary: "Q4 revenue by region:
      North America: $4.2M (+12%)
      EMEA: $2.8M (+7%)
      APAC: $1.9M (+23%)",
    posted_to: "#analytics",
    tokens_used: 847
  }
}`;

function ScrollReveal({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function highlightPython(line: string): string {
  const tokens: { start: number; end: number; cls: string }[] = [];

  const patterns: [RegExp, string][] = [
    [/#.*$/gm, "text-[#8b7ab8]"],
    [/"""[\s\S]*?"""/g, "text-[#6ee7b7]"],
    [/f?"(?:[^"\\]|\\.)*"/g, "text-[#6ee7b7]"],
    [/\b(?:import|from|as|def|class|return|if|else|elif|for|in|while|with|try|except|finally|raise|yield|lambda|and|or|not|is|None|True|False|pass|break|continue)\b/g, "text-[#d8b4fe]"],
    [/\b(?:print|init|run)\b(?=\s*\()/g, "text-[#7dd3fc]"],
    [/\b(?:qosm)\b/g, `text-[${accent.DEFAULT}]`],
    [/\b\d+\b/g, "text-[#fdba74]"],
    [/[=(),{}:]/g, "text-[#8b7ab8]"],
  ];

  for (const [re, cls] of patterns) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line)) !== null) {
      tokens.push({ start: m.index, end: m.index + m[0].length, cls });
    }
  }

  tokens.sort((a, b) => a.start - b.start || b.end - a.end);
  const filtered: typeof tokens = [];
  let lastEnd = 0;
  for (const t of tokens) {
    if (t.start >= lastEnd) {
      filtered.push(t);
      lastEnd = t.end;
    }
  }

  let result = "";
  let pos = 0;
  for (const t of filtered) {
    if (t.start > pos) {
      result += escapeHtml(line.slice(pos, t.start));
    }
    result += `<span class="${t.cls}">${escapeHtml(line.slice(t.start, t.end))}</span>`;
    pos = t.end;
  }
  if (pos < line.length) {
    result += escapeHtml(line.slice(pos));
  }
  return result;
}

function highlightConsole(line: string): string {
  if (line.startsWith("> ")) {
    return `<span class="text-[#8b7ab8]">&gt;</span> <span class="text-[#f5f3ff]">${escapeHtml(line.slice(2))}</span>`;
  }
  if (line.startsWith("Resolving")) {
    return `<span style="color:${accent.DEFAULT}">${escapeHtml(line)}</span>`;
  }
  if (line.trimStart().startsWith("Capabilities:") || line.trimStart().startsWith("Attenuations:")) {
    const indent = line.match(/^(\s*)/)?.[0] || "";
    const rest = line.trimStart();
    const colonIdx = rest.indexOf(":");
    const key = rest.slice(0, colonIdx + 1);
    const val = rest.slice(colonIdx + 1);
    return `${escapeHtml(indent)}<span class="text-[#8b7ab8]">${escapeHtml(key)}</span><span class="text-[#c4b5fd]">${escapeHtml(val)}</span>`;
  }
  if (line.startsWith("Ok {")) {
    return `<span class="text-emerald-400">${escapeHtml(line)}</span>`;
  }
  if (line === "}") {
    return `<span class="text-emerald-400">}</span>`;
  }
  if (line.includes(":")) {
    const match = line.match(/^(\s*)(\w+)(:)(.*)/);
    if (match) {
      return `${escapeHtml(match[1])}<span class="text-[#c4b5fd]">${escapeHtml(match[2])}</span><span class="text-[#8b7ab8]">${escapeHtml(match[3])}</span><span class="text-[#6ee7b7]">${escapeHtml(match[4])}</span>`;
    }
  }
  return `<span class="text-[#6ee7b7]">${escapeHtml(line)}</span>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export default function EmbedSection() {
  return (
    <section className="py-24 px-4 bg-bg-elevated max-md:py-16">
      <div className="max-w-[1000px] mx-auto">
        <div className="text-center mb-12 max-md:mb-8">
          <ScrollReveal>
            <h2 className="text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-text-primary tracking-tight mb-4">
              Qosm runs <span style={{ color: "#fbbf24" }}>anywhere</span>.
            </h2>
            <p className="text-lg text-text-secondary max-w-[600px] mx-auto leading-relaxed max-md:text-base">
              Run on our servers or embed the runtime in yours.
              Five lines of Python to go from capabilities to typed results.
            </p>
          </ScrollReveal>
        </div>

        <ScrollReveal delay={100}>
          <div className="flex gap-4 items-stretch max-md:flex-col max-md:gap-3">
            {/* Python code */}
            <div className="flex-1 min-w-0 rounded-xl overflow-hidden border border-border">
              <div className="bg-bg-surface px-4 py-2.5 flex justify-between items-center border-b border-border">
                <span className="font-mono text-xs text-text-muted">main.py</span>
                <span className="text-[0.6rem] font-semibold uppercase tracking-widest text-text-muted">
                  Python
                </span>
              </div>
              <div className="bg-bg-inset">
                <pre className="m-0 px-5 pt-4 pb-4 text-[13px] leading-relaxed overflow-x-auto max-md:text-sm max-md:px-4">
                  <code className="font-mono">
                    {pythonCode.split("\n").map((line, i) => (
                      <div
                        key={i}
                        className="text-text-primary"
                        dangerouslySetInnerHTML={{
                          __html: highlightPython(line) || "&nbsp;",
                        }}
                      />
                    ))}
                  </code>
                </pre>
              </div>
            </div>

            {/* Console output */}
            <div className="flex-1 min-w-0 rounded-xl overflow-hidden border border-border">
              <div className="bg-bg-surface px-4 py-2.5 flex justify-between items-center border-b border-border">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-border" />
                    <span className="w-2.5 h-2.5 rounded-full bg-border" />
                    <span className="w-2.5 h-2.5 rounded-full bg-border" />
                  </div>
                  <span className="font-mono text-xs text-text-muted">terminal</span>
                </div>
                <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
              </div>
              <div className="bg-bg-inset">
                <pre className="m-0 px-5 pt-4 pb-4 text-[13px] leading-relaxed overflow-x-auto max-md:text-sm max-md:px-4">
                  <code className="font-mono">
                    {consoleOutput.split("\n").map((line, i) => (
                      <div
                        key={i}
                        dangerouslySetInnerHTML={{
                          __html: highlightConsole(line) || "&nbsp;",
                        }}
                      />
                    ))}
                  </code>
                </pre>
              </div>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          <div className="grid grid-cols-2 gap-3 max-w-[500px] mx-auto mt-8 max-md:grid-cols-1">
            <div className="rounded-lg border border-border bg-bg-inset px-4 py-3 text-center">
              <span className="text-xs font-semibold text-text-primary">Qosm Cloud</span>
              <p className="text-[11px] text-text-muted mt-1">Run on our servers. API call in, typed result out.</p>
            </div>
            <div className="rounded-lg border border-dashed border-border bg-bg-inset px-4 py-3 text-center">
              <span className="text-xs font-semibold text-text-muted">Embedded Runtime</span>
              <p className="text-[11px] text-text-dim mt-1">Bundle the interpreter in Python, TypeScript, Go, or Rust.</p>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={300}>
          <div className="flex justify-center gap-6 mt-6 max-md:flex-wrap max-md:gap-3">
            {["Python", "TypeScript", "Go", "Rust"].map((lang, i) => (
              <span
                key={lang}
                className={`font-mono text-xs font-medium px-3 py-1.5 rounded-md ${
                  i <= 1
                    ? ""
                    : "bg-bg-surface text-text-dim"
                }`}
                style={i <= 1 ? { backgroundColor: accent.subtle, color: accent.DEFAULT } : undefined}
              >
                {lang}{i > 1 && " (soon)"}
              </span>
            ))}
          </div>
        </ScrollReveal>

      </div>
    </section>
  );
}
